/**
 * KEGAGALAN SIMPAN SETELAN TOKO → kalimat yang bisa DITINDAKLANJUTI admin toko.
 * Helper MURNI, tanpa I/O. Diuji di `__tests__/settingsWriteError.test.ts`.
 *
 * ── KENAPA BERKAS INI ADA ───────────────────────────────────────────────────
 *
 * Gejalanya (log PROD 2026-09-05, admin toko natasya-shop): menyimpan setelan
 * kadang berhasil, kadang gagal dengan
 *
 *     store_id wajib untuk site_settings: tidak ada konteks toko
 *     (jalur tanpa-konteks harus set store_id eksplisit)
 *
 * Itu teks `raise` milik trigger `set_store_id_from_context` (mig 3090/3240).
 * Bunyinya seolah menyuruh KODE mengirim `store_id` — dan itulah salah baca
 * yang harus dicegah berkas ini, karena mengirim `store_id` eksplisit TIDAK
 * memperbaiki apa pun di jalur ini:
 *
 *   · `site_settings.store_id` NOT NULL tanpa default, di-stamp trigger BEFORE
 *     INSERT dari `current_store_id()`;
 *   · `current_store_id()` NULL hanya bila `auth.uid()` NULL — yakni permintaan
 *     terkirim TANPA sesi;
 *   · dan policy tulisnya menuntut `is_admin()`, yang di repo ini store-scoped
 *     (`profiles.store_id = current_store_id()`). Tanpa sesi, `is_admin()`
 *     false — jadi baris itu akan ditolak RLS (42501) walau `store_id` diisi
 *     sendiri. Trigger cuma kebetulan bicara lebih dulu.
 *
 * Artinya pesan itu SELALU berarti satu hal bagi pemakai: **permintaannya
 * terkirim TANPA sesi**, lalu ditolak. Apa pun sebabnya di sisi klien, yang bisa
 * dilakukan admin toko cuma satu — login ulang — dan teks trigger tak
 * memberitahunya sama sekali.
 *
 * ⚠️ Jangan menuliskan sebabnya di sini sebagai "refresh token ditolak". Versi
 * pertama berkas ini melakukannya dan KELIRU: log `/auth/v1/token` menunjukkan
 * refresh di jendela yang sama justru berhasil. Sebab persisnya masih terbuka —
 * rinciannya (termasuk hipotesis yang masih berdiri) di
 * `docs/GOTCHA-operasional.md` §H.
 *
 * Tempatnya BUKAN di satu layar: `site_settings` ditulis banyak editor setelan.
 * Satu penerjemah bersama = satu kalimat yang sama di semua layar, dan satu
 * tempat untuk diperbaiki kalau bunyinya berubah.
 */

/**
 * Kalimat pengganti. Menyebutkan bahwa isian TIDAK hilang adalah bagian dari
 * pesannya, bukan basa-basi: state formulir masih di memori tab, jadi admin
 * yang login ulang di tab lain bisa kembali dan menekan Simpan sekali lagi —
 * tanpa kalimat ini ia akan mengira ketikannya lenyap dan mengulang dari nol.
 */
export const SETTINGS_SESSION_LOST_MESSAGE =
  "Sesi admin sudah berakhir, jadi perubahan belum tersimpan. Login ulang di tab lain, " +
  "lalu tekan Simpan lagi di halaman ini — isian Anda masih ada selama tab ini tidak ditutup.";

/**
 * Kalimat saat TOKO tak ter-resolusi di klien (`getClientStoreId()` null).
 *
 * Ini BUKAN kembaran pesan di atas, dan bedanya penting: yang di atas berarti
 * "kami tahu tokonya, tapi kamu tak lagi dikenali"; yang ini berarti "kami tak
 * tahu ini toko yang mana". Keduanya berujung pada layar yang TIDAK BOLEH
 * menyajikan formulir yang bisa disimpan — menyimpan setelan bawaan ke atas
 * konfigurasi nyata jauh lebih merusak daripada menolak memuat.
 */
export const SETTINGS_STORE_UNRESOLVED_MESSAGE =
  "Toko tidak dikenali dari alamat yang dibuka, jadi setelan tidak dimuat. " +
  "Buka panel admin lewat domain toko Anda, lalu coba lagi.";

/** Ambil `message` + `code` dari error supabase-js / Error biasa. */
function errorParts(err: unknown): { message: string; code: string } {
  if (!err) return { message: "", code: "" };
  if (typeof err === "string") return { message: err, code: "" };
  if (typeof err !== "object") return { message: String(err), code: "" };
  const rec = err as { message?: unknown; code?: unknown };
  return {
    message: typeof rec.message === "string" ? rec.message : "",
    code: typeof rec.code === "string" ? rec.code : "",
  };
}

/**
 * true bila kegagalan tulis `site_settings` berarti "permintaan tak membawa
 * sesi bertoko".
 *
 * DUA bentuk, karena mana yang muncul bergantung baris yang ditulis: kunci yang
 * BELUM ada memicu INSERT → trigger stamp bicara lebih dulu (`23502` + teks
 * "tidak ada konteks toko"); kunci yang sudah ada tetap melewati INSERT (upsert
 * `on conflict` menjalankan trigger BEFORE INSERT lebih dulu), tapi jalur lain
 * ke tabel yang sama berhenti di policy → `42501`. Keduanya satu sebab bagi
 * pemakai, jadi keduanya satu kalimat.
 */
export function isLostStoreContextError(err: unknown): boolean {
  const { message, code } = errorParts(err);
  const text = message.toLowerCase();
  if (text.includes("tidak ada konteks toko")) return true;
  if (text.includes("konteks toko ditolak")) return true;
  // 42501 = insufficient_privilege; PostgREST memakainya untuk pelanggaran RLS.
  if (code === "42501") return true;
  return text.includes("row-level security");
}

/**
 * Pesan gagal-simpan yang layak ditampilkan. Error lain DIBIARKAN apa adanya —
 * menyeragamkan semua kegagalan jadi satu kalimat ramah akan menyembunyikan
 * galat nyata (kolom hilang, jaringan putus) dari orang yang bisa melaporkannya.
 */
export function describeSettingsWriteError(err: unknown): string {
  if (isLostStoreContextError(err)) return SETTINGS_SESSION_LOST_MESSAGE;
  const { message } = errorParts(err);
  return message.trim() || "Gagal menyimpan.";
}
